import { useEffect } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { ContactShadows, OrbitControls, Grid } from "@react-three/drei";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { CameraRig } from "./CameraRig";
import { markSceneReady } from "./loadProgress";
import { Environment3D } from "./Environment3D";
import { FpsWatchdog } from "./FpsWatchdog";
import { FirstPersonControls } from "./FirstPersonControls";
import { WALKABLE_OFFICE_COLLIDERS } from "./WalkableOffice";
import { PHOTOREAL_OFFICE_WALK_COLLIDERS } from "./photorealWalkBounds";
import { PackPinProjector, type PinScreen } from "./PackPinProjector";
import { DeskWorkstation, type DeskPin } from "./DeskWorkstation";
import { ConferenceTv, type TvPin } from "./ConferenceTv";
import { VideoScreen } from "./VideoScreen";
import { OpenLaptop } from "./OpenLaptop";
import { QrStand } from "./QrStand";
import { StaticGltfProp } from "./StaticGltfProp";
import { ReceptionTerminal, type TerminalPin } from "./ReceptionTerminal";
import { DeskPhone } from "./DeskPhone";
import { EntryIntercom, type IntercomPin } from "./EntryIntercom";
import { DeskPackage, type PackagePin } from "./DeskPackage";
import { OfficePosters, type PosterLinkPin } from "./OfficePosters";
import { PrivacyRoomProps, type PrivacyItemPin, type LaptopStickyPin } from "./PrivacyRoomProps";
import { CounterHotspot, type CounterHotspotPin } from "./CounterHotspot";
import { ArcadeMarkers, type ArcadeSpotPin } from "./ArcadeMarkers";
import type {
  ArcadeSpotSpec as ArcSpotSpec,
  ArcadeTvSpec as ArcTvSpec,
  ScreenQuadSpec as ArcScreenQuad,
} from "../scenario/types";
import { GltfScreenOverlay, parseScreenQuadOverride } from "./GltfScreenOverlay";
import type {
  DeviceTier,
  EnvironmentSpec,
  PackItemSpec,
  PrivacyFurnitureItem,
  PrivacyRoomItem,
  ScenarioSpec,
  ScreenBox,
} from "../scenario/types";

/** Equirect pin yaw offset reconciles the pano mesh rotation with our texel→dir
 *  math. Uses env.packPinYawOffsetDeg (calibrated per world), falling back to
 *  panoRotationYDeg; `?pinYaw=` overrides for live calibration (one object
 *  aligned → all align, since it's a rigid rotation). */
function packPinYawOffsetDeg(env: EnvironmentSpec): number {
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("pinYaw");
    if (q != null && q !== "") return Number(q);
  }
  return env.packPinYawOffsetDeg ?? env.panoRotationYDeg ?? 0;
}

/** Azimuth angle (radians, three.js OrbitControls convention) that the pano
 *  spin-in-place rig is centred on for this waypoint — mirrors the offset
 *  PanoOrbitBootstrap bakes into camera.position (eye - dir*d), so a yaw limit
 *  clamps exactly around the beat's own forward direction. */
function panoAzimuthCenter(eye: [number, number, number], lookTarget: [number, number, number]): number {
  const dir = new THREE.Vector3(lookTarget[0] - eye[0], lookTarget[1] - eye[1], lookTarget[2] - eye[2]);
  if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1);
  else dir.normalize();
  return Math.atan2(-dir.x, -dir.z);
}

/** `?pinMirror=-1` flips longitude handedness during calibration. */
function packPinMirror(): number {
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("pinMirror");
    if (q === "-1") return -1;
  }
  return 1;
}

/** When OrbitControls target == eye (pano spin-in-place), place the camera on
 *  the OPPOSITE side of the intended look so controls (which always look at the
 *  target) face the desk — not 180° flipped into the wardrobe. */
function PanoOrbitBootstrap({
  enabled,
  eye,
  lookTarget,
  fov,
}: {
  enabled: boolean;
  eye: [number, number, number];
  lookTarget: [number, number, number];
  /** Optional FOV for look-at presets (zoom without leaving the pano bubble). */
  fov?: number;
}) {
  const { camera } = useThree();
  useEffect(() => {
    if (!enabled) return;
    const dir = new THREE.Vector3(
      lookTarget[0] - eye[0],
      lookTarget[1] - eye[1],
      lookTarget[2] - eye[2],
    );
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1);
    else dir.normalize();
    const d = 0.06;
    // Camera behind the look → OrbitControls looks toward eye along +dir.
    camera.position.set(eye[0] - dir.x * d, eye[1] - dir.y * d, eye[2] - dir.z * d);
    camera.lookAt(eye[0] + dir.x, eye[1] + dir.y, eye[2] + dir.z);
    if (fov && camera instanceof THREE.PerspectiveCamera) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  }, [enabled, eye[0], eye[1], eye[2], lookTarget[0], lookTarget[1], lookTarget[2], fov, camera]);
  return null;
}

/** Snaps the camera straight to a waypoint's own position/target once (no
 *  tween — inspect/look-only mode has no CameraRig running to fly it there).
 *  Needed because OrbitControls only ORBITS whatever pose the camera is
 *  already at; it never repositions the camera to match a new `target` prop
 *  by itself. Distinct from PanoOrbitBootstrap (pano's "eye == target,
 *  derive position from lookTarget" spin-in-place math) — this is a plain
 *  waypoint with its own authored position AND target, so it snaps both
 *  directly with no offset math. */
function InspectSnap({
  enabled,
  position,
  target,
  fov,
}: {
  enabled: boolean;
  position: [number, number, number];
  target: [number, number, number];
  fov?: number;
}) {
  const { camera } = useThree();
  useEffect(() => {
    if (!enabled) return;
    camera.position.set(position[0], position[1], position[2]);
    camera.lookAt(target[0], target[1], target[2]);
    if (fov && camera instanceof THREE.PerspectiveCamera) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  }, [enabled, position[0], position[1], position[2], target[0], target[1], target[2], fov, camera]);
  return null;
}

/** Debug: append ?free=1 to free-orbit the camera (and show grid/axes) so a new
 *  Marble world's orientation/scale can be tuned, then baked to splatTransform. */
const FREE_ORBIT =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).get("free") === "1";

const SPLAT_READY_FLAG = import.meta.env.VITE_SPLAT_READY === "true";

interface Props {
  scenario: ScenarioSpec;
  /** the active environment (multi-scene courses CUT between rooms). */
  environment: EnvironmentSpec;
  tier: DeviceTier;
  waypointId: string | null;
  onArrived?: () => void;
  /** runtime auto-demote callback when sustained FPS is too low */
  onDemote?: () => void;
  /** accessibility: shorten/snap camera moves */
  reducedMotion?: boolean;
  /** Learner-facing Inspect mode (OrbitControls around current waypoint). */
  inspectMode?: boolean;
  /** Pack-in-3D: project these pins onto the pano while looking around. */
  packItems?: PackItemSpec[];
  onPackPins?: (pins: PinScreen[]) => void;
  /** Walkable desk-PC: report the sticky-note screen position each frame. */
  onDeskPin?: (pin: DeskPin) => void;
  /** Walkable conference TV: report the screen position + whether it's on. */
  onTvPin?: (pin: TvPin) => void;
  tvOn?: boolean;
  /** Reception welcome-kiosk terminal: report the screen position each frame.
   *  Unlike deskPc/conferenceTv this is NOT gated on scenario.walkable — it's
   *  a real 3D prop in the room regardless of the camera-control mode. */
  onTerminalPin?: (pin: TerminalPin) => void;
  /** Wall-mounted entry intercom near the door: report the screen position each
   *  frame (same non-walkable-gated treatment as the reception terminal). */
  onIntercomPin?: (pin: IntercomPin) => void;
  /** Episode 13 desk phone: pulse the ring LED + glow halo while true (driven
   *  by App from the active beat). No pin — answering is a DOM overlay. */
  phoneRinging?: boolean;
  /** Desk package (unexpected USB delivery): report the sticky-note screen
   *  position each frame. Same non-walkable-gated treatment as the terminal/
   *  intercom — a real 3D prop that stays on the desk regardless of camera
   *  control mode. */
  onPackagePin?: (pin: PackagePin) => void;
  /** Episode 10 reuses the SAME Desk A laptop rendered by `PrivacyRoomProps`
   *  (no second/duplicate laptop prop) as "your own laptop": reports the
   *  sticky note's screen position each frame. Same non-walkable-gated
   *  treatment as the terminal/intercom/package. */
  onLoungePin?: (pin: LaptopStickyPin) => void;
  /** Wall poster gallery: only the ONE poster carrying `linkUrl` reports a
   *  click pin each frame (same non-walkable-gated treatment as the other
   *  real 3D props above). */
  onPosterLinkPin?: (pin: PosterLinkPin) => void;
  /** Bar Wi-Fi game: an invisible click-prompt anchor (e.g. "Order a drink"
   *  near the bartender) that ends a free-roam beat. Same non-walkable-gated
   *  treatment as the other real-prop pins above. */
  onCounterPin?: (pin: CounterHotspotPin) => void;
  /** Episode-3-style "find the risks" 3D document props (real props in the
   *  room, not screen-% hotspots) — same non-walkable-gated treatment as the
   *  terminal/intercom/package: they report a pin every frame regardless of
   *  camera-control mode. */
  privacyItems?: PrivacyRoomItem[];
  privacyFurniture?: PrivacyFurnitureItem[];
  privacyFoundIds?: Set<string>;
  onPrivacyPins?: (pins: PrivacyItemPin[]) => void;
  /** Learner-toggled free first-person exploration on an otherwise guided
   *  course (`scenario.allowFreeExplore`). Unlike `scenario.walkable`, this
   *  ONLY swaps the camera control (CameraRig -> FirstPersonControls) — it
   *  does NOT enable the walkable-only procedural props (DeskWorkstation/
   *  ConferenceTv) or any of the walkable storyboard UI in App.tsx. */
  freeExplore?: boolean;
  /** "Look around from a fixed spot" for a PROCEDURAL (non-pano) room, e.g.
   *  the privacy hunt's look-only default (see App.tsx). Reuses the pano
   *  viewer's "eye == target, spin without moving" orbit math instead of the
   *  generic inspect-an-object orbit (which lets the camera swing through
   *  the room's own walls — see PanoOrbitBootstrap/panoLikeOrbit below). */
  spinInPlace?: boolean;
  /** Reports the projected screen-space box for `environment.screenQuad` each
   *  frame, so a DOM overlay (e.g. the hacker MITM reveal) can be glued to a
   *  world-space 3D screen surface instead of hand-tuned per-viewport CSS. */
  onScreenBox?: (box: ScreenBox) => void;
  /** Beat-level screenQuad: several screens in one room (arcade cabinets, TV). */
  beatScreenQuad?: ArcScreenQuad;
  arcadeSpots?: ArcSpotSpec[];
  arcadeTv?: ArcTvSpec;
  arcadeHub?: boolean;
  onArcadePins?: (pins: Record<string, ArcadeSpotPin>) => void;
  /** Half-angle (degrees) the learner may yaw away from the waypoint's own
   *  forward direction while `spinInPlace` is active — unlike a real pano
   *  capture (which is a full 360 photo), a procedural room's real glTF
   *  geometry has walls/doorways/windows only a couple of metres from a
   *  "stand here and look around" point, so a full spin can turn to face
   *  a doorway into another room, or a window showing the exterior skybox,
   *  reading as "we walked outside". Required whenever `spinInPlace` is set. */
  spinYawLimitDeg?: number;
  /** Pause the WebGL rAF loop (full-screen HTML video overlays). Leaving the
   *  canvas running under a briefing video fights the decoder and desyncs
   *  baked voice from picture. */
  paused?: boolean;
}

/**
 * The WebGL scene layer. Renders the (tiered) environment, lighting, soft
 * shadows, and the choreographed camera. The interactive DOM device shells are
 * composited OVER this canvas by App (sharper + accessible than in-scene UI).
 */
export function Scene({
  scenario,
  environment,
  tier,
  waypointId,
  onArrived,
  onDemote,
  reducedMotion,
  inspectMode,
  packItems,
  onPackPins,
  onDeskPin,
  onTvPin,
  tvOn,
  onTerminalPin,
  onIntercomPin,
  phoneRinging,
  onPackagePin,
  onLoungePin,
  onCounterPin,
  privacyItems,
  privacyFurniture,
  privacyFoundIds,
  onPrivacyPins,
  onPosterLinkPin,
  freeExplore,
  spinInPlace,
  spinYawLimitDeg,
  onScreenBox,
  paused,
  beatScreenQuad,
  arcadeSpots,
  arcadeTv,
  arcadeHub,
  onArcadePins,
}: Props) {
  const wp = scenario.scene.waypoints.find((w) => w.id === waypointId) ?? null;
  const initial =
    scenario.scene.waypoints.find((w) => w.id === scenario.scene.initialWaypointId) ??
    scenario.scene.waypoints[0];
  const activeWp = wp ?? initial;
  const orbitTarget = activeWp?.target ?? ([0, 1, 0] as [number, number, number]);

  // `scenarioWalkable` keeps gating the walkable-ONLY procedural props below
  // (DeskWorkstation/ConferenceTv); `walkable` (camera-control choice) also
  // goes true on a learner-toggled `freeExplore`, without touching those.
  const scenarioWalkable = !!scenario.walkable;
  const walkable = scenarioWalkable || !!freeExplore;
  const freeRoam = FREE_ORBIT || !!scenario.freeRoam;
  const useOrbit = !walkable && (freeRoam || !!inspectMode);
  const isSplat = !!(environment.enableSplat && SPLAT_READY_FLAG && !environment.enablePano);
  const isPano = !!environment.enablePano;
  const isGltfRoom = !!environment.gltfUrl;
  const isImmersive = isSplat || isPano || isGltfRoom;
  // Real pano photo OR a procedural room forced into "spin in place" mode —
  // both want the camera POSITION pinned and only the look direction to
  // change (see PanoOrbitBootstrap). Kept separate from `isPano` everywhere
  // else (lighting/exposure/fog stay keyed on the REAL asset type).
  const panoLikeOrbit = isPano || !!spinInPlace;
  // Daylight exterior (pool deck): OutdoorSky owns the sun, the sky-derived
  // environment map and the ground, so the indoor three-light rig below stands
  // down. Opt-in per environment, so every other course is untouched.
  const outdoor = environment.outdoor;
  const outdoorShadows = !!outdoor?.shadows;

  return (
    <Canvas
      className="sim-canvas"
      frameloop={paused ? "never" : "always"}
      shadows={!isImmersive || outdoorShadows}
      // Outdoor decks already fill the screen with a sun + shadows + refractive
      // water. A 2x retina buffer on top of that is what pushed Chrome past 2 GB;
      // cap pixel ratio so other courses keep their existing sharpness.
      dpr={outdoor ? [1, 1.25] : tier === "high" ? [1, 2] : [1, 1.5]}
      gl={{
        antialias: !outdoor,
        toneMapping: THREE.ACESFilmicToneMapping,
        powerPreference: "high-performance",
      }}
      camera={{
        position: initial?.position ?? [2.6, 1.9, 2.8],
        fov: initial?.fov ?? 50,
        // Outdoors the sky cubemap and the horizon sit far past the interior
        // far plane, so both ends of the frustum open up.
        near: outdoor ? 0.1 : 0.05,
        far: outdoor ? 400 : isImmersive ? 200 : 100,
      }}
      onCreated={({ gl, scene }) => {
        const backdrop = new THREE.Color(environment.backdropColor ?? "#0d1014");
        gl.setClearColor(backdrop);
        gl.toneMappingExposure =
          environment.exposure ?? (isPano ? 1.35 : isImmersive ? 1.15 : 1.05);
        // Indoor IBL. OutdoorSky installs its own sky cubemap — generating a
        // RoomEnvironment here as well leaked a second PMREM (~tens of MB) and
        // lit the deck with interior bounce, which is why shadows vanished.
        if (!outdoor) {
          const pmrem = new THREE.PMREMGenerator(gl);
          scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        }
        // Fog washes Marble pano/splat into a beige smear — procedural only.
        scene.fog = isImmersive ? null : new THREE.Fog(backdrop, 14, 46);
        requestAnimationFrame(() => markSceneReady());
      }}
    >
      {!outdoor && (
        <>
          <hemisphereLight
            args={
              isGltfRoom
                ? ["#fff2df", "#7a5f42", 0.42]
                : ["#eaf3fb", "#b59a72", isImmersive ? 0.25 : 0.55]
            }
          />
          <directionalLight
            position={[4, 6, 3]}
            intensity={isGltfRoom ? 0.85 : isImmersive ? 0.6 : 2.1}
            color={isGltfRoom ? "#ffdca8" : "#fff2dc"}
            castShadow={!isImmersive}
            shadow-mapSize={[2048, 2048]}
            shadow-bias={-0.0002}
            shadow-normalBias={0.02}
          />
          <directionalLight
            position={[-4, 3, -2]}
            intensity={isGltfRoom ? 0.28 : 0.35}
            color={isGltfRoom ? "#ffe9c8" : "#cfe4ff"}
          />
        </>
      )}

      <Environment3D env={environment} tier={tier} />
      <SceneBackdrop
        color={environment.backdropColor ?? "#0d1014"}
        fog={!isImmersive}
        exposure={environment.exposure ?? (isPano ? 1.35 : isImmersive ? 1.15 : 1.05)}
      />

      {!isImmersive && (
        <ContactShadows position={[0, 0.005, 0]} opacity={0.5} scale={12} blur={2.4} far={4} />
      )}

      {walkable ? (
        <FirstPersonControls
          enabled
          colliders={
            environment.gltfUrl
              ? environment.gltfColliders ?? []
              : isSplat || environment.enableSplat
              ? PHOTOREAL_OFFICE_WALK_COLLIDERS
              : WALKABLE_OFFICE_COLLIDERS
          }
          spawn={
            (activeWp?.position as [number, number, number] | undefined) ??
            (isSplat || environment.enableSplat
              ? [0.4, 1.55, 1.6]
              : [0, 0, 2.4])
          }
          eyeHeight={
            activeWp?.position?.[1] ??
            (isSplat || environment.enableSplat ? 1.55 : 1.65)
          }
          lookTarget={activeWp?.target as [number, number, number] | undefined}
          tweenMs={activeWp?.durationS && activeWp.durationS > 0 ? activeWp.durationS * 1000 : undefined}
          fov={activeWp?.fov}
          moveSpeed={isSplat || environment.enableSplat ? 3.6 : 3.2}
          // glTF rooms: gate movement on real floor mesh so the learner can
          // never walk out of the modelled office into the exterior void.
          floorGated={isGltfRoom}
          walkSteps={!!scenario.walkSteps}
        />
      ) : useOrbit ? (
        <>
          {FREE_ORBIT && (
            <>
              <axesHelper args={[2]} />
              <Grid args={[20, 20]} cellSize={1} sectionSize={5} infiniteGrid fadeDistance={40} />
            </>
          )}
          {panoLikeOrbit && activeWp?.position && activeWp?.target && (
            <PanoOrbitBootstrap
              enabled
              eye={activeWp.position as [number, number, number]}
              lookTarget={activeWp.target as [number, number, number]}
              fov={activeWp.fov}
            />
          )}
          {!panoLikeOrbit && !isSplat && activeWp?.position && activeWp?.target && (
            <InspectSnap
              enabled
              position={activeWp.position as [number, number, number]}
              target={activeWp.target as [number, number, number]}
              fov={activeWp.fov}
            />
          )}
          <OrbitControls
            // Remount on look-preset changes so internal spherical state doesn't
            // fight PanoOrbitBootstrap (otherwise Look at barely moves the view).
            key={panoLikeOrbit ? `pano-${waypointId ?? "x"}` : freeRoam ? "free" : "orbit"}
            makeDefault
            // Pano/spin-in-place: spin at capture centre — never dolly through
            // walls. Splat freeRoam: pan + dolly through the Marble volume (Spark PoC).
            target={panoLikeOrbit ? (activeWp?.position ?? orbitTarget) : orbitTarget}
            enablePan={freeRoam}
            enableZoom={freeRoam || (!panoLikeOrbit && !isSplat)}
            minDistance={panoLikeOrbit ? 0.05 : freeRoam && isSplat ? 0.25 : 0.8}
            maxDistance={freeRoam ? 40 : panoLikeOrbit ? 0.08 : 3.5}
            // spinInPlace gets its OWN (tighter) polar range than a real pano
            // capture: a procedural room's real ceiling/floor are only ~1.5m
            // away from a standing eye height, so the pano-calibrated range
            // (which allows tilting up near-vertical for stitched sky/zenith
            // shots) puts the near clip plane inside the ceiling structure up
            // close. Clamp closer to a natural standing eye-level look.
            minPolarAngle={freeRoam && isSplat ? 0.15 : spinInPlace ? 0.9 : panoLikeOrbit || isSplat ? 0.45 : 0.2}
            maxPolarAngle={
              freeRoam && isSplat
                ? Math.PI - 0.15
                : spinInPlace
                ? Math.PI / 2 + 0.35
                : panoLikeOrbit || isSplat
                ? Math.PI / 2 - 0.12
                : Math.PI / 2 - 0.05
            }
            {...(!freeRoam && isPano && environment.panoYawLimitDeg && activeWp?.position && activeWp?.target
              ? (() => {
                  const center = panoAzimuthCenter(
                    activeWp.position as [number, number, number],
                    activeWp.target as [number, number, number],
                  );
                  const limit = (environment.panoYawLimitDeg * Math.PI) / 180;
                  return { minAzimuthAngle: center - limit, maxAzimuthAngle: center + limit };
                })()
              : {})}
            {...(spinInPlace && spinYawLimitDeg && activeWp?.position && activeWp?.target
              ? (() => {
                  const center = panoAzimuthCenter(
                    activeWp.position as [number, number, number],
                    activeWp.target as [number, number, number],
                  );
                  const limit = (spinYawLimitDeg * Math.PI) / 180;
                  return { minAzimuthAngle: center - limit, maxAzimuthAngle: center + limit };
                })()
              : {})}
          />
        </>
      ) : (
        <CameraRig waypoint={activeWp} onArrived={onArrived} reducedMotion={reducedMotion} />
      )}

      {onDemote && <FpsWatchdog enabled={tier !== "low"} onDemote={onDemote} />}

      {environment.deskPc && onDeskPin && (
        <DeskWorkstation spec={environment.deskPc} onPin={onDeskPin} />
      )}

      {environment.conferenceTv && onTvPin && (
        <ConferenceTv spec={environment.conferenceTv} on={!!tvOn} onPin={onTvPin} />
      )}

      {environment.videoScreens?.map((v, i) => (
        <VideoScreen key={i} spec={v} />
      ))}

      {environment.laptops?.map((l, i) => (
        <OpenLaptop key={i} spec={l} />
      ))}

      {environment.qrStands?.map((q, i) => (
        <QrStand key={i} spec={q} />
      ))}

      {environment.staticGltfProps?.map((p, i) => (
        <StaticGltfProp key={i} spec={p} />
      ))}

      {environment.receptionTerminal && onTerminalPin && (
        <ReceptionTerminal spec={environment.receptionTerminal} onPin={onTerminalPin} />
      )}

      {environment.deskPhone && (
        <DeskPhone spec={environment.deskPhone} ringing={!!phoneRinging} />
      )}

      {environment.entryIntercom && onIntercomPin && (
        <EntryIntercom spec={environment.entryIntercom} onPin={onIntercomPin} />
      )}

      {environment.deskPc && environment.deskPackage && onPackagePin && (
        <DeskPackage deskSpec={environment.deskPc} spec={environment.deskPackage} onPin={onPackagePin} />
      )}

      {environment.officePosters && environment.officePosters.length > 0 && (
        <OfficePosters specs={environment.officePosters} onLinkPin={onPosterLinkPin} />
      )}

      {environment.counterHotspot && onCounterPin && (
        <CounterHotspot spec={environment.counterHotspot} onPin={onCounterPin} />
      )}

      {arcadeSpots && arcadeSpots.length > 0 && (
        <ArcadeMarkers spots={arcadeSpots} hubActive={!!arcadeHub} tv={arcadeTv} onPins={onArcadePins} />
      )}

      {environment.screenQuad && onScreenBox && (
        <GltfScreenOverlay
          quad={parseScreenQuadOverride() ?? beatScreenQuad ?? environment.screenQuad}
          onBox={onScreenBox}
        />
      )}

      {environment.wallInfills?.map((w, i) => (
        <mesh key={`wall-infill-${i}`} position={w.position}>
          <boxGeometry args={w.size} />
          <meshStandardMaterial color={w.color ?? "#22262b"} roughness={0.92} metalness={0.02} />
        </mesh>
      ))}

      {/* Furniture alone is enough to mount this: a course can reuse the lounge
          desk/laptop props (and the laptop's clickable sticky note) without
          running the privacy hunt, i.e. with zero `items`. */}
      {((privacyItems && privacyItems.length > 0) || (privacyFurniture && privacyFurniture.length > 0)) &&
        onPrivacyPins && (
          <PrivacyRoomProps
            items={privacyItems ?? []}
            furniture={privacyFurniture}
            foundIds={privacyFoundIds ?? new Set()}
            onPins={onPrivacyPins}
            onLaptopPin={onLoungePin}
          />
        )}

      {packItems && packItems.length > 0 && onPackPins && (
        <PackPinProjector
          items={packItems}
          eye={(activeWp?.position as [number, number, number]) ?? [0, 1.5, 0]}
          yawOffsetDeg={packPinYawOffsetDeg(environment)}
          mirror={packPinMirror()}
          onUpdate={onPackPins}
        />
      )}
    </Canvas>
  );
}

function SceneBackdrop({
  color,
  fog = true,
  exposure,
}: {
  color: string;
  fog?: boolean;
  exposure?: number;
}) {
  const { gl, scene } = useThree();
  useEffect(() => {
    const c = new THREE.Color(color);
    gl.setClearColor(c);
    scene.fog = fog ? new THREE.Fog(c, 14, 46) : null;
    // Re-apply on every environment CUT (Canvas onCreated only fires once).
    if (typeof exposure === "number") gl.toneMappingExposure = exposure;
  }, [color, fog, exposure, gl, scene]);
  return null;
}
